import { useState } from "react";
import { useFetch } from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader } from "@/mockups/ui";
import MonthGrid from "@/components/rrhh/MonthGrid";
import RequestVacationDialog from "@/components/rrhh/RequestVacationDialog";
import CalendarioEquipo from "@/pages/rrhh/CalendarioEquipo";
import Vacaciones from "@/pages/rrhh/Vacaciones";
import Festivos from "@/pages/rrhh/Festivos";
import GoogleCalendarConnect from "@/components/rrhh/GoogleCalendarConnect";
import { cn } from "@/lib/utils";
import { Plane, CalendarOff, Star, CheckSquare, Plus, X, CalendarDays, Users, Plane as PlaneIcon } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const ICON = { vacaciones: Plane, ausencia: CalendarOff, festivo: Star, tarea: CheckSquare };
const fmtEs = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("es-ES", { day: "2-digit", month: "short" });

function chipClass(e) {
  if (e.type === "festivo") return "bg-gold/15 text-[#a9741c] dark:text-gold border-transparent";
  if (e.type === "tarea") return "bg-primary/12 text-primary border-transparent";
  if (e.status === "pending") return "border-dashed border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300";
  if (e.type === "ausencia") return "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-500/15 dark:text-orange-300";
  return "bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-500/15 dark:text-sky-300";
}

function MiCalendario() {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const month = `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}`;
  const { data, loading, refetch } = useFetch("/hr/calendar/agenda", { month });
  const [sel, setSel] = useState({ from: null, to: null });
  const [dialogOpen, setDialogOpen] = useState(false);

  const onDayClick = (d) => {
    setSel((s) => {
      if (!s.from || (s.from && s.to)) return { from: d, to: null };
      if (d < s.from) return { from: d, to: null };
      return { from: s.from, to: d };
    });
  };

  const renderEvent = (e, i) => {
    const Icon = ICON[e.type] || Star;
    const label = e.type === "festivo" ? e.label : e.type === "tarea" ? e.label : `${e.label}${e.status === "pending" ? " · pend." : ""}`;
    return (
      <div key={i} className={cn("flex items-center gap-1 truncate rounded border px-1.5 py-0.5 text-[11px]", chipClass(e))} title={label} data-testid={`agenda-event-${e.type}`}>
        <Icon className="h-3 w-3 shrink-0" /><span className="truncate">{label}</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <MonthGrid data={data} loading={loading} cursor={cursor} setCursor={setCursor} renderEvent={renderEvent} onDayClick={onDayClick} selectedRange={sel} />

      <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-sky-400" /> Vacaciones</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-orange-400" /> Ausencia</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded border border-dashed border-amber-400 bg-amber-50" /> Solicitado</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-gold" /> Festivo</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-primary" /> Tarea (vencimiento)</span>
      </div>

      {sel.from && (
        <Card className="border-primary/30 bg-primary/[0.03] shadow-none p-4 flex flex-wrap items-center justify-between gap-3 sticky bottom-4" data-testid="agenda-selection-bar">
          <span className="text-sm">Seleccionado: <strong>{fmtEs(sel.from)}{sel.to && sel.to !== sel.from ? ` → ${fmtEs(sel.to)}` : ""}</strong></span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => setSel({ from: null, to: null })}><X className="h-4 w-4" /> Limpiar</Button>
            <Button size="sm" className="gap-1.5" onClick={() => setDialogOpen(true)} data-testid="agenda-request-btn"><Plus className="h-4 w-4" /> Solicitar estos días</Button>
          </div>
        </Card>
      )}

      {dialogOpen && (
        <RequestVacationDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          initialFrom={sel.from || ""}
          initialTo={sel.to || sel.from || ""}
          onSaved={() => { setSel({ from: null, to: null }); refetch(); }}
        />
      )}
    </div>
  );
}

export default function MiAgenda() {
  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="mi-agenda-page">
      <PageHeader title="Mi agenda" subtitle="Tu calendario, el del equipo y tus vacaciones, todo en uno." />

      <GoogleCalendarConnect />

      <Tabs defaultValue="calendario" className="w-full">
        <TabsList data-testid="agenda-tabs">
          <TabsTrigger value="calendario" className="gap-1.5" data-testid="agenda-tab-calendario"><CalendarDays className="h-4 w-4" /> Mi calendario</TabsTrigger>
          <TabsTrigger value="equipo" className="gap-1.5" data-testid="agenda-tab-equipo"><Users className="h-4 w-4" /> Equipo</TabsTrigger>
          <TabsTrigger value="vacaciones" className="gap-1.5" data-testid="agenda-tab-vacaciones"><PlaneIcon className="h-4 w-4" /> Vacaciones</TabsTrigger>
          <TabsTrigger value="festivos" className="gap-1.5" data-testid="agenda-tab-festivos"><Star className="h-4 w-4" /> Festivos</TabsTrigger>
        </TabsList>

        <TabsContent value="calendario" className="mt-6"><MiCalendario /></TabsContent>
        <TabsContent value="equipo" className="mt-6"><CalendarioEquipo embedded /></TabsContent>
        <TabsContent value="vacaciones" className="mt-6"><Vacaciones embedded /></TabsContent>
        <TabsContent value="festivos" className="mt-6"><Festivos embedded /></TabsContent>
      </Tabs>
    </div>
  );
}
